// Hosted (RAG) agent subscription — real one-time Razorpay payment per
// billing period (order → Checkout.js → verified confirm), same pattern as
// the existing comment-unlock flow. See lib/razorpay.ts.
import { apiSlice } from './apiSlice'
import { ENDPOINTS } from '../../lib/endpoints'

export interface AgentSubscriptionPlan {
  id: string
  name: string
  billingCycle: 'monthly' | 'quarterly' | 'yearly'
  priceInPaise: number
  gstPct: number
  isActive: boolean
  createdAt: string
}

export interface AgentSubscription {
  id: string
  agentId: string
  planId: string
  status: 'pending' | 'active' | 'grace' | 'deactivated' | 'terminated' | 'cancelled'
  currentPeriodEnd: string | null
  graceEndDate: string | null
  deactivatedAt: string | null
  createdAt: string
  updatedAt: string
  plan: AgentSubscriptionPlan
}

export interface AgentSubscriptionOrder {
  orderId: string
  amount: number
  currency: string
  keyId: string
}

export interface AgentSubscriptionInvoice {
  id: string
  invoiceNumber: string
  issuedAt: string
  totalPaise: number
  taxableValuePaise: number
  cgstPaise: number
  sgstPaise: number
  igstPaise: number
  isPlaceholder: boolean
  pdfUrl: string | null
}

export interface MySubscriptionInvoice {
  id: string
  invoiceNumber: string
  issuedAt: string
  totalPaise: number
  isPlaceholder: boolean
  agentName: string
}

export const agentSubscriptionsApi =apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAgentSubscriptionPlans: builder.query<{ plans: AgentSubscriptionPlan[] }, string>({
      query: (agentId) => ENDPOINTS.agentSubscriptions.plans(agentId),
    }),

    getAgentSubscription: builder.query<{ subscription: AgentSubscription | null }, string>({
      query: (agentId) => ENDPOINTS.agentSubscriptions.subscription(agentId),
      providesTags: ['AgentSubscription'],
    }),

    createAgentSubscriptionOrder: builder.mutation<AgentSubscriptionOrder, { agentId: string; planId: string }>({
      query: ({ agentId, planId }) => ({
        url: ENDPOINTS.agentSubscriptions.order(agentId),
        method: 'POST',
        body: { planId },
      }),
    }),

    confirmAgentSubscriptionPayment: builder.mutation<{ subscription: AgentSubscription }, {
      agentId: string; planId: string
      razorpayOrderId: string; razorpayPaymentId: string; razorpaySignature: string
      buyerGstin?: string; buyerState?: string
    }>({
      query: ({ agentId, ...body }) => ({
        url: ENDPOINTS.agentSubscriptions.confirm(agentId),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AgentSubscription'],
    }),

    getAgentSubscriptionInvoices: builder.query<{ invoices: AgentSubscriptionInvoice[] }, string>({
      query: (agentId) => ENDPOINTS.agentSubscriptions.invoices(agentId),
      providesTags: ['AgentSubscription'],
    }),

    // Every hosting-subscription invoice across the caller's own agents (Settings → Billing).
    getMySubscriptionInvoices: builder.query<{ invoices: MySubscriptionInvoice[] }, void>({
      query: () => ENDPOINTS.agentSubscriptions.myInvoices,
      providesTags: ['AgentSubscription'],
      keepUnusedDataFor: 0,
    }),
  }),
})

export const {
  useGetAgentSubscriptionPlansQuery,
  useGetAgentSubscriptionQuery,
  useCreateAgentSubscriptionOrderMutation,
  useConfirmAgentSubscriptionPaymentMutation,
  useGetAgentSubscriptionInvoicesQuery,
  useGetMySubscriptionInvoicesQuery,
} = agentSubscriptionsApi
